<script>
    import { createEventDispatcher, onMount } from 'svelte';
    import { AuthManager } from './helpers/AuthManager.js';
    import { isLoggedIn } from './helpers/BookingState.js';
    import { getCustomerId, getCurrentBooking, getCarModels } from './helpers/Api.js';
    export let distance = 500;
    export let address = '';
    export let language = '';

    let addressEmpty = false;
    export let autoBookEnabled = false;
    export let closestCarEnabled = false;
    let userscriptDetected = false;
    let availableModels = [];
    export let selectedModels = [];
    let modelsDropdownOpen = false;

    const dispatch = createEventDispatcher();

    async function fetchCustomerId() {
        try {
            const token = AuthManager.authTokens.access_token;
            if (token) {
                const id = await getCustomerId(token);
                if (id) {
                    let customerId = id;
                }
                let booking = await getCurrentBooking(token, id);

            }
        } catch (error) {
            // Error fetching customer ID
        }
    }

    function clearAddress() {
        address = '';
        addressEmpty = false;
        dispatch('message', {
            address: '',
            autoBookEnabled,
            selectedModels
        });
    }

    onMount(async () => {
        // Initial check
        userscriptDetected = typeof window.retrieveCommunautoTokens === 'function';
        
        // If not detected immediately, set up periodic checks
        if (!userscriptDetected) {
            // Check every 300ms for up to 2 seconds
            let checkCount = 0;
            const checkInterval = setInterval(() => {
                userscriptDetected = typeof window.retrieveCommunautoTokens === 'function';
                checkCount++;


                if (userscriptDetected || checkCount >= 7) {
                    clearInterval(checkInterval);
                }
            }, 300);
        }

        // Load available car models
        try {
            availableModels = await getCarModels();
        } catch (error) {
            // Error loading car models
        }

        // Add click outside handler
        document.addEventListener('click', handleClickOutside);
        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    });

    function validate() {
        window.plausible('Search');
        if (address) {
            if (distance <= 100) {
                window.plausible('< 100m');
            } else if (distance >= 800) {
                window.plausible('> 800m');
            }
            dispatch('message', {
                distance,
                address,
                autoBookEnabled,
                closestCarEnabled,
                selectedModels
            });
        } else {
            addressEmpty = true;
        }
    }

    function toggleModel(model) {
        if (selectedModels.includes(model)) {
            selectedModels = selectedModels.filter(m => m !== model);
        } else {
            selectedModels = [...selectedModels, model];
        }
    }

    function selectAllModels() {
        selectedModels = [...availableModels];
    }

    function clearSelectedModels() {
        selectedModels = [];
    }

    function handleClickOutside(event) {
        if (!event.target.closest('.models-dropdown')) {
            modelsDropdownOpen = false;
        }
    }
</script>

<div class="w-full max-w-md mx-auto">
    <div class="space-y-6">
        <!-- Address Input -->
        <div>
            <label class="block text-sm font-medium text-gray-700 mb-1" for="Address">
                {language == "FR" ? "Adresse" : "Address"}
            </label>
            <div class="relative">
                <input
                    bind:value={address}
                    class="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-commugreen focus:border-transparent outline-none transition-all"
                    class:border-red-500={addressEmpty}
                    id="address"
                    type="text"
                    placeholder={language == "FR" ? "Entrez une adresse" : "Enter an address"}
                >
                {#if address}
                    <button
                        type="button"
                        class="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        on:click={clearAddress}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                            <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
                        </svg>
                    </button>
                {/if}
            </div>
            {#if addressEmpty}
                <p class="mt-1 text-sm text-red-600">
                    {language == "FR" ? "Entrez une adresse" : "Please enter an address."}
                </p>
            {/if}
        </div>

        <!-- Distance Input -->
        <div>
            <label class="block text-sm font-medium text-gray-700 mb-1" for="Distance">
                {language == "FR" ? "Distance maximale (en mètres)" : "Maximum distance (in meters)"}
            </label>
            <input
                bind:value={distance}
                class="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-commugreen focus:border-transparent outline-none transition-all"
                id="distance"
                type="number"
                placeholder="500"
            >
        </div>

        <!-- Checkboxes -->
        {#if userscriptDetected}
        <div class="space-y-3">
            <div class="flex items-center">
                <input
                    id="autoBookEnabled"
                    type="checkbox"
                    bind:checked={autoBookEnabled}
                    on:change={() => {
                        if(autoBookEnabled) {
                            window.plausible('Auto Booking Enabled');
                        } else {
                            window.plausible('Auto Booking Disabled');
                        }
                    }}
                    disabled={!$isLoggedIn}
                    class="h-4 w-4 text-commugreen border-gray-300 rounded focus:ring-commugreen disabled:opacity-50"
                >
                <label
                    class="ml-2 text-sm text-gray-700"
                    class:opacity-50={!$isLoggedIn}
                    for="autoBookEnabled"
                >
                    {language == "FR" ? "Réservation automatique" : "Auto booking"}
                </label>
            </div>

            {#if autoBookEnabled && $isLoggedIn}
            <div class="flex items-center">
                <input
                    id="closestCarEnabled"
                    type="checkbox"
                    bind:checked={closestCarEnabled}
                    class="h-4 w-4 text-commugreen border-gray-300 rounded focus:ring-commugreen"
                >
                <label class="ml-2 text-sm text-gray-700" for="closestCarEnabled">
                    {language == "FR" ? "Voiture la plus proche" : "Closest car"}
                </label>
                <div class="tooltip-wrap ml-2">
                    <span class="cursor-help text-gray-400 text-sm">ⓘ</span>
                    <div class="tooltip-box">
                        {#if language == "FR"}
                            Après chaque réservation, le rayon est réduit à 90% de la distance de la voiture. Si une voiture plus proche apparaît, la réservation est annulée et la nouvelle voiture est réservée.<br>Rayon minimum : 50m.
                        {:else}
                            After each booking the radius shrinks to 90% of the car's distance. If a closer car appears within the new radius, the booking is cancelled and the closer car is booked.<br>Minimum radius: 50m.
                        {/if}
                    </div>
                </div>
            </div>
            {/if}
        </div>
        {/if}

        <!-- Car Models Filter -->
        {#if availableModels.length > 0}
        <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">
                {language == "FR" ? "Modèles de voiture" : "Car Models"}
                {#if selectedModels.length > 0}
                    <span class="text-xs text-gray-500">({selectedModels.length} selected)</span>
                {/if}
            </label>
            <div class="relative models-dropdown">
                <button
                    type="button"
                    on:click={() => modelsDropdownOpen = !modelsDropdownOpen}
                    class="w-full px-4 py-2 text-left bg-white border border-gray-300 rounded-lg hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-commugreen focus:border-transparent"
                >
                    <span class="block truncate">
                        {#if selectedModels.length === 0}
                            {language == "FR" ? "Tous les modèles" : "All models"}
                        {:else if selectedModels.length === 1}
                            {selectedModels[0]}
                        {:else}
                            {selectedModels.length} {language == "FR" ? "modèles sélectionnés" : "models selected"}
                        {/if}
                    </span>
                    <span class="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                        <svg class="h-5 w-5 text-gray-400 transition-transform" class:rotate-180={modelsDropdownOpen} viewBox="0 0 20 20" fill="currentColor">
                            <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd" />
                        </svg>
                    </span>
                </button>

                {#if modelsDropdownOpen}
                    <div class="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto">
                        <div class="sticky top-0 bg-gray-50 px-4 py-2 border-b flex gap-2">
                            <button
                                type="button"
                                on:click={selectAllModels}
                                class="text-xs px-2 py-1 bg-commugreen text-white rounded hover:bg-commugreen-dark"
                            >
                                {language == "FR" ? "Tout" : "All"}
                            </button>
                            <button
                                type="button"
                                on:click={clearSelectedModels}
                                class="text-xs px-2 py-1 bg-gray-500 text-white rounded hover:bg-gray-600"
                            >
                                {language == "FR" ? "Aucun" : "None"}
                            </button>
                        </div>
                        {#each availableModels as model}
                            <label class="flex items-center px-4 py-2 hover:bg-gray-50 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={selectedModels.includes(model)}
                                    on:change={() => toggleModel(model)}
                                    class="h-4 w-4 text-commugreen border-gray-300 rounded focus:ring-commugreen"
                                >
                                <span class="ml-2 text-sm text-gray-700">{model}</span>
                            </label>
                        {/each}
                    </div>
                {/if}
            </div>
        </div>
        {/if}

        <!-- Buttons -->
        <div class="space-y-3">
            {#if userscriptDetected && !$isLoggedIn}
                <button
                    type="button"
                    on:click={() => {
                        window.plausible('Login Button Clicked');
                        AuthManager.openCommunauto();
                    }}
                    class="w-full px-4 py-2 text-white bg-gray-600 hover:bg-gray-700 rounded-lg transition-colors"
                >
                    {language == "FR" ? "Se connecter" : "Login"}
                </button>
            {/if}

            <button
                on:click={validate}
                class="w-full px-4 py-2 text-white bg-commugreen hover:bg-commugreen-dark rounded-lg transition-colors"
                type="button"
            >
                {language == "FR" ? "Chercher" : "Search"}
            </button>
        </div>
    </div>
</div>

<style>
    input[type="checkbox"] {
        cursor: pointer;
    }
    input[type="checkbox"]:disabled {
        cursor: not-allowed;
    }

    .tooltip-wrap {
        position: relative;
        display: inline-flex;
        align-items: center;
    }
    .tooltip-box {
        display: none;
        position: absolute;
        bottom: calc(100% + 4px);
        left: 0;
        width: 16rem;
        font-size: 0.75rem;
        background: #1f2937;
        color: white;
        border-radius: 0.25rem;
        padding: 0.5rem;
        z-index: 10;
    }
    .tooltip-wrap:hover .tooltip-box {
        display: block;
    }
</style>
